import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';
import { useSupabase } from '../../../contexts/SupabaseContext';
import axios from 'axios';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import SellIcon from '@mui/icons-material/Sell';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';
import EditSaleDialog from '../../../components/stocks/EditSaleDialog';
import { toDateString, insertSaleRecord, deleteSale } from '../../../utils/stockSales';
import AmountField from '../../../components/AmountField';
// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};
// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f',
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b',
];

// Update the getYahooFinanceUrl function
const getYahooFinanceUrl = (symbol) => {
  // Convert symbol to Yahoo Finance format
  let yahooSymbol = symbol;
  if (!symbol.includes('.')) {
    // Check if it's a BSE stock (usually 5 digits)
    if (/^\d{5}$/.test(symbol)) {
      yahooSymbol = `${symbol}.BO`; // .BO for BSE
    } else {
      yahooSymbol = `${symbol}.NS`; // .NS for NSE
    }
  }
  // Proxy through our own /api/yahoo-chart function (Yahoo has no CORS headers)
  const yahooUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${yahooSymbol}?interval=1d&range=1d`;
  return `/api/yahoo-chart?url=${encodeURIComponent(yahooUrl)}`;
};

// Cost-weighted average purchase date of the lots a sale uses (FIFO, same order as
// calculateFIFOCostBasis). The lots are reduced or deleted on sale, so this is
// stored with the sale to work out its annualised return later.
const getFIFOAverageBuyDate = (stock, unitsToSell) => {
  const lots = [...(stock?.transactions || [])].sort((a, b) =>
    new Date(a.purchaseDate) - new Date(b.purchaseDate)
  );
  let remaining = unitsToSell;
  let weightedTime = 0;
  let totalCost = 0;
  for (const lot of lots) {
    if (remaining <= 0) break;
    const units = Math.min(parseFloat(lot.units), remaining);
    const cost = units * lot.price;
    const time = new Date(`${lot.purchaseDate}T00:00:00`).getTime();
    if (!cost || Number.isNaN(time)) continue;
    weightedTime += cost * time;
    totalCost += cost;
    remaining -= units;
  }
  return totalCost > 0 ? toDateString(new Date(weightedTime / totalCost)) : null;
};

const Stocks = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-equity-stocks', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockTransactions, setStockTransactions] = useState([]);

  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  // Edit form state
  const [editPrice, setEditPrice] = useState('');
  const [editAmountInvested, setEditAmountInvested] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  const [editUnits, setEditUnits] = useState('');

  // Sell stock state
  const [openSellModal, setOpenSellModal] = useState(false);
  const [selectedStockForSale, setSelectedStockForSale] = useState(null);
  const [sellPrice, setSellPrice] = useState('');
  const [sellUnits, setSellUnits] = useState('');
  const [sellDate, setSellDate] = useState(new Date());
  const [sellCharges, setSellCharges] = useState('0');
  const [sellTotalAmount, setSellTotalAmount] = useState('');
  const [sellNetAmount, setSellNetAmount] = useState('');
  const [sellProfitLoss, setSellProfitLoss] = useState('');

  // Edit sale state: id of the stock_sales row being edited (EditSaleDialog)
  const [editingSaleId, setEditingSaleId] = useState(null);
  const [openDeleteSaleDialog, setOpenDeleteSaleDialog] = useState(false);

  // Form state
  const [stockName, setStockName] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [price, setPrice] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [units, setUnits] = useState('');

  // User's saved stock names and live price states
  const [userStockNames, setUserStockNames] = useState([]);
  const [livePrices, setLivePrices] = useState({});
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  const [priceError, setPriceError] = useState(null);
  const [currentTotalValue, setCurrentTotalValue] = useState(0);

  // Dummy data for Stocks
  const dummyData = [
    { id: 1, name: 'Stock A', value: 200000 },
    { id: 2, name: 'Stock B', value: 150000 },
    { id: 3, name: 'Stock C', value: 100000 },
  ];

  // Group investments by stock name
  const groupInvestmentsByStock = (data) => {
    const groupedMap = data.reduce((acc, investment) => {
      if (!acc[investment.name]) {
        acc[investment.name] = {
          name: investment.name,
          ticker: investment.ticker || '',
          totalValue: 0,
          totalUnits: 0,
          transactions: []
        };
      }

      acc[investment.name].totalValue += investment.value;
      acc[investment.name].totalUnits += parseFloat(investment.units || 0);
      acc[investment.name].transactions.push(investment);

      return acc;
    }, {});

    // Convert the grouped map to an array
    return Object.values(groupedMap).map((stock, index) => ({
      id: index + 1,
      name: stock.name,
      ticker: stock.ticker,
      value: stock.totalValue,
      units: stock.totalUnits,
      transactions: stock.transactions
    }));
  };

  // Function to fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch investments');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      console.log('Fetching stock investments for user ID:', user.id);
      const { data, error } = await supabase
        .from('stocks_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });

      if (error) {
        throw error;
      }

      console.log('Fetched stock data:', data);

      if (data && data.length > 0) {
        // Transform the data to match the expected format for the component
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          ticker: item.ticker,
          value: item.amount_invested,
          price: item.price,
          purchaseDate: item.purchase_date,
          units: item.units
        }));

        // Group investments by stock name
        const groupedInvestments = groupInvestmentsByStock(formattedData);

        setInvestments(groupedInvestments);
        const total = formattedData.reduce((sum, item) => sum + item.value, 0);
        setTotalValue(total);
        console.log('Total Stock value:', total);

        // Extract unique stock names from user's investments
        const uniqueStockNames = [...new Set(data.map(item => item.name))];
        setUserStockNames(uniqueStockNames);
      } else {
        console.log('No stock investments found');
        setInvestments([]);
        setTotalValue(0);
        setUserStockNames([]);
      }
    } catch (error) {
      console.error('Error fetching investments:', error);
      // Fallback to empty data in case of error
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  // Initial data fetch and connection test
  useEffect(() => {
    const init = async () => {
      try {
        // Test Supabase connection
        console.log('Testing Supabase connection...');
        const { data: connectionTest, error: connectionError } = await supabase
          .from('stocks_investments')
          .select('count')
          .limit(1);

        if (connectionError) {
          console.error('Supabase connection error:', connectionError);
        } else {
          console.log('Supabase connection successful:', connectionTest);
        }

        // Fetch investments
        await fetchInvestments();
      } catch (error) {
        console.error('Initialization error:', error);
        setLoading(false);
      }
    };

    init();
  }, [fetchInvestments]);

  // Calculate amount when price or units changes
  useEffect(() => {
    if (price && units && parseFloat(price) > 0 && parseFloat(units) > 0) {
      setAmountInvested((parseFloat(price) * parseFloat(units)).toFixed(2));
    } else {
      setAmountInvested('');
    }
  }, [price, units]);

  // Calculate amount when price or units changes for edit modal
  useEffect(() => {
    if (editPrice && editUnits && parseFloat(editPrice) > 0 && parseFloat(editUnits) > 0) {
      setEditAmountInvested((parseFloat(editPrice) * parseFloat(editUnits)).toFixed(2));
    } else {
      setEditAmountInvested('');
    }
  }, [editPrice, editUnits]);

  // Helper function to calculate FIFO cost basis
  const calculateFIFOCostBasis = useMemo(() => {
    if (!selectedStockForSale || !selectedStockForSale.transactions || !sellUnits || parseFloat(sellUnits) <= 0) {
      return 0;
    }

    // Sort transactions by purchase date (oldest first for FIFO)
    const sortedTransactions = [...selectedStockForSale.transactions].sort((a, b) =>
      new Date(a.purchaseDate) - new Date(b.purchaseDate)
    );

    let remainingUnitsToSell = parseFloat(sellUnits);
    let totalCostBasis = 0;

    console.log('=== FIFO Calculation ===');
    console.log('Units to sell:', remainingUnitsToSell);
    console.log('Sorted transactions:', sortedTransactions.map(t => ({
      date: t.purchaseDate,
      units: t.units,
      price: t.price
    })));

    // Calculate cost basis using FIFO
    for (const transaction of sortedTransactions) {
      if (remainingUnitsToSell <= 0) break;

      const transactionUnits = parseFloat(transaction.units);
      const unitsFromThisTransaction = Math.min(transactionUnits, remainingUnitsToSell);
      const pricePerUnit = transaction.price;
      const costFromThisTransaction = unitsFromThisTransaction * pricePerUnit;

      console.log(`Taking ${unitsFromThisTransaction} units @ ₹${pricePerUnit} = ₹${costFromThisTransaction}`);

      totalCostBasis += costFromThisTransaction;
      remainingUnitsToSell -= unitsFromThisTransaction;
    }

    console.log('Total FIFO Cost Basis:', totalCostBasis);
    console.log('Average Buy Price (FIFO):', totalCostBasis / parseFloat(sellUnits));
    console.log('======================');

    return totalCostBasis;
  }, [selectedStockForSale, sellUnits]);

  // Calculate sell amounts when price, units, or charges change
  useEffect(() => {
    if (sellPrice && sellUnits && parseFloat(sellPrice) > 0 && parseFloat(sellUnits) > 0) {
      const totalAmount = parseFloat(sellPrice) * parseFloat(sellUnits);
      setSellTotalAmount(totalAmount.toFixed(2));

      const charges = parseFloat(sellCharges) || 0;
      const netAmount = totalAmount - charges;
      setSellNetAmount(netAmount.toFixed(2));

      // Calculate profit/loss using FIFO cost basis
      const costBasis = calculateFIFOCostBasis;
      const profitLoss = netAmount - costBasis;
      setSellProfitLoss(profitLoss.toFixed(2));
    } else {
      setSellTotalAmount('');
      setSellNetAmount('');
      setSellProfitLoss('');
    }
  }, [sellPrice, sellUnits, sellCharges, calculateFIFOCostBasis]);

  // Calculate pie chart data for both current and invested values
  const pieChartData = useMemo(() => {
    const currentData = investments.map((investment) => {
      const livePrice = livePrices[investment.name]?.price;
      const currentValue = livePrice
        ? investment.units * livePrice
        : investment.value;

      return {
        id: investment.id,
        value: currentValue,
        label: investment.name,
        percentage: ((currentValue / currentTotalValue) * 100).toFixed(1) + '%'
      };
    }).sort((a, b) => b.value - a.value);

    const investedData = investments.map((investment) => ({
      id: investment.id,
      value: investment.value,
      label: investment.name,
      percentage: ((investment.value / totalValue) * 100).toFixed(1) + '%'
    })).sort((a, b) => b.value - a.value);

    return { currentData, investedData };
  }, [investments, livePrices, currentTotalValue, totalValue]);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleReset = () => {
    setStockName('');
    setPurchaseDate(new Date());
    setPrice('');
    setUnits('');
    setAmountInvested('');
  };

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }

    if (!stockName || !purchaseDate || !price || !units) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);

      // Get the stock name (either from string input or from selected option)
      const stockNameValue = typeof stockName === 'string' ? stockName : stockName.label || stockName;

      // Prepare the data for Supabase - explicitly set ticker to null
      const newInvestment = {
        name: stockNameValue,
        ticker: null, // Setting ticker explicitly to null
        purchase_date: purchaseDate.toISOString().split('T')[0],
        price: parseFloat(price),
        amount_invested: parseFloat(amountInvested),
        units: parseFloat(units),
        user_id: user.id
      };

      console.log('Creating new investment:', newInvestment);

      // Insert data into Supabase
      const { data, error } = await supabase
        .from('stocks_investments')
        .insert([newInvestment])
        .select();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert response:', data);

      // Add the new investment to the list and update total value
      if (data && data.length > 0) {
        // Refresh the data from Supabase
        await fetchInvestments();
      }

      // Reset form and close modal
      handleReset();
      handleCloseModal();

    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Reload a stock's purchases from the database and show them, with its sales, in the
  // history popup (the stock object captured when the popup opened goes stale after edits).
  // Closes the popup if the stock has no purchases or sales left.
  const refreshStockHistory = async (stockName) => {
    const { data, error } = await supabase
      .from('stocks_investments')
      .select('*')
      .eq('user_id', user.id)
      .eq('name', stockName);
    if (error) throw error;
    const transactions = (data || []).map(item => ({
      id: item.id,
      name: item.name,
      ticker: item.ticker,
      value: item.amount_invested,
      price: item.price,
      purchaseDate: item.purchase_date,
      units: item.units
    }));
    const { count: salesCount } = await supabase
      .from('stock_sales')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('stock_name', stockName);
    if (transactions.length === 0 && !salesCount) {
      handleCloseHistoryModal();
      return;
    }
    await handleOpenTransactionHistory({
      ...(selectedStock || {}),
      name: stockName,
      transactions,
      value: transactions.reduce((sum, t) => sum + (Number(t.value) || 0), 0),
      units: transactions.reduce((sum, t) => sum + (parseFloat(t.units) || 0), 0),
    });
  };

  const handleOpenTransactionHistory = async (stock) => {
    setSelectedStock(stock);
    
    // Fetch both purchase and sale transactions
    try {
      // Get purchase transactions
      const purchaseTransactions = stock.transactions || [];
      
      // Get sale transactions from stock_sales table
      const { data: saleTransactions, error } = await supabase
        .from('stock_sales')
        .select('*')
        .eq('user_id', user.id)
        .eq('stock_name', stock.name)
        .order('sale_date', { ascending: false });
      
      if (error) {
        console.error('Error fetching sale transactions:', error);
      }
      
      // Combine and format all transactions
      const allTransactions = [
        // Purchase transactions
        ...purchaseTransactions.map(tx => ({
          id: tx.id,
          type: 'buy',
          name: stock.name,
          date: tx.purchaseDate,
          purchaseDate: tx.purchaseDate,
          price: tx.price,
          units: tx.units,
          amount: tx.value,
          value: tx.value,
          notes: ''
        })),
        // Sale transactions
        ...(saleTransactions || []).map(tx => ({
          id: `sale-${tx.id}`,
          type: 'sell',
          date: tx.sale_date,
          price: tx.sale_price,
          units: -tx.units_sold, // Negative to show it's a sale
          amount: tx.net_amount,
          charges: tx.charges,
          profitLoss: tx.profit_loss,
          averageBuyPrice: tx.average_buy_price,
          avgBuyDate: tx.avg_buy_date,
          notes: ''
        }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date, newest first
      
      setStockTransactions(allTransactions);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setStockTransactions(stock.transactions || []);
    }
    
    setOpenHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedStock(null);
    setStockTransactions([]);
  };

  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditModalOpen = () => {
    handleTransactionMenuClose();

    if (!currentTransaction) {
      console.error('No transaction selected for editing');
      return;
    }

    console.log('Editing transaction:', currentTransaction);

    // Set form values with defaults in case of null/undefined values
    setEditPrice(currentTransaction.price ? currentTransaction.price.toString() : '');
    setEditAmountInvested(currentTransaction.value ? currentTransaction.value.toString() : '');
    setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
    setEditUnits(currentTransaction.units ? currentTransaction.units.toString() : '0');

    setOpenEditModal(true);
  };

  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };

  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleUpdateTransaction = async () => {
    if (!editPrice || !editUnits || !editPurchaseDate) {
      alert('Please fill all required fields');
      return;
    }

    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }

    if (!(currentTransaction.name || selectedStock?.name)) {
      alert('Cannot update transaction: the stock name is missing. Close and reopen the stock, then try again.');
      return;
    }

    try {
      setLoading(true);

      // Prepare the data for Supabase - ensure proper data types
      const updatedTransaction = {
        name: currentTransaction.name || selectedStock?.name,
        ticker: null, // Set ticker explicitly to null
        purchase_date: toDateString(editPurchaseDate),
        price: parseFloat(editPrice),
        amount_invested: parseFloat(editAmountInvested),
        units: parseFloat(editUnits),
      };

      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);

      // Update data in Supabase
      const { data, error } = await supabase
        .from('stocks_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update response:', data);

      // Refresh the data from Supabase
      await fetchInvestments();

      // Also update the transaction list in the history popup if it's open
      if (selectedStock && openHistoryModal) {
        await refreshStockHistory(selectedStock.name);
      }

      // Close the edit modal
      handleEditModalClose();

    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);

      console.log('Deleting transaction:', currentTransaction.id);

      // Delete from Supabase
      const { error } = await supabase
        .from('stocks_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh the data from Supabase
      await fetchInvestments();

      // Also update the transaction list in the history popup if it's open
      if (selectedStock && openHistoryModal) {
        await refreshStockHistory(selectedStock.name);
      }

      // Close the delete dialog
      handleDeleteDialogClose();

    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Sale transaction handlers
  const handleEditSaleModalOpen = () => {
    handleTransactionMenuClose();
    if (!currentTransaction || currentTransaction.type !== 'sell') {
      console.error('No sale transaction selected for editing');
      return;
    }
    // Transaction ids for sales are "sale-{stock_sales id}"
    setEditingSaleId(currentTransaction.id.replace('sale-', ''));
  };

  const handleSaleEdited = async ({ unitsChanged, unitsDifference }) => {
    setEditingSaleId(null);
    await fetchInvestments();
    if (selectedStock) {
      await refreshStockHistory(selectedStock.name);
    }
    if (unitsChanged) {
      const n = Math.abs(unitsDifference);
      alert(`Sale updated. ${n} unit${n === 1 ? '' : 's'} ${unitsDifference > 0 ? 'removed from' : 'returned to'} your holdings.`);
    } else {
      alert('Sale updated.');
    }
  };

  const handleDeleteSaleDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteSaleDialog(true);
  };

  const handleDeleteSaleDialogClose = () => {
    setOpenDeleteSaleDialog(false);
  };

  const handleDeleteSale = async () => {
    if (!currentTransaction || currentTransaction.type !== 'sell') {
      console.error('Invalid sale transaction for deletion');
      return;
    }

    try {
      setLoading(true);
      // Transaction ids for sales are "sale-{stock_sales id}"
      const { unitsRestored } = await deleteSale({
        supabase,
        userId: user.id,
        saleId: currentTransaction.id.replace('sale-', ''),
      });

      await fetchInvestments();
      if (selectedStock) {
        await refreshStockHistory(selectedStock.name);
      }

      handleDeleteSaleDialogClose();
      alert(`Sale deleted. ${unitsRestored} unit${unitsRestored === 1 ? '' : 's'} returned to your holdings.`);
    } catch (error) {
      console.error('Error deleting sale:', error);
      alert(`Failed to delete sale: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchLivePrices = useCallback(async () => {
    setPriceError(null);

    try {
      console.log('Starting fetchLivePrices...');

      // Get unique stock symbols from investments
      const uniqueStocks = [...new Set(investments.map(inv => inv.name))];
      console.log('Unique stocks to fetch:', uniqueStocks);

      if (uniqueStocks.length === 0) {
        console.log('No stocks to fetch prices for');
        return;
      }

      const newPrices = {};
      let hasValidPrices = false;

      // Process stocks in batches to avoid rate limiting
      const batchSize = 2;
      for (let i = 0; i < uniqueStocks.length; i += batchSize) {
        const batch = uniqueStocks.slice(i, i + batchSize);
        console.log(`Processing batch ${i / batchSize + 1}:`, batch);

        try {
          // Add a longer delay between batches
          if (i > 0) {
            console.log('Adding delay between batches...');
            await new Promise(resolve => setTimeout(resolve, 3000));
          }

          // Fetch prices for the batch in parallel
          const promises = batch.map(async (stock) => {
            try {
              const url = getYahooFinanceUrl(stock);
              console.log(`Fetching price for ${stock} using URL:`, url);

              const response = await axios.get(url, {
                headers: {
                  'Accept': 'application/json'
                },
                timeout: 15000
              });

              // Parse the response data if it's a string
              let data;
              try {
                data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
              } catch (parseError) {
                console.error(`Error parsing response for ${stock}:`, parseError);
                return null;
              }

              console.log(`Response for ${stock}:`, data);

              if (data?.chart?.result?.[0]) {
                const result = data.chart.result[0];
                const quote = result.meta;

                if (!quote) {
                  console.log(`No quote data for ${stock}`);
                  return null;
                }

                const regularMarketPrice = quote.regularMarketPrice;
                const previousClose = quote.chartPreviousClose;

                if (regularMarketPrice && previousClose) {
                  const change = regularMarketPrice - previousClose;
                  const changePercent = (change / previousClose) * 100;

                  return {
                    stock,
                    price: regularMarketPrice,
                    change,
                    changePercent,
                    currency: quote.currency || 'INR',
                    lastUpdated: new Date().toISOString()
                  };
                } else {
                  console.log(`Missing price data for ${stock}:`, { regularMarketPrice, previousClose });
                }
              } else {
                console.log(`Invalid response format for ${stock}:`, data);
              }
              return null;
            } catch (error) {
              console.error(`Error fetching price for ${stock}:`, error.message);
              if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
              }
              return null;
            }
          });

          const results = await Promise.all(promises);
          console.log('Batch results:', results);

          results.forEach((result) => {
            if (result) {
              newPrices[result.stock] = {
                price: result.price,
                change: result.change,
                changePercent: result.changePercent,
                currency: result.currency,
                lastUpdated: result.lastUpdated
              };
              hasValidPrices = true;
            }
          });

        } catch (error) {
          console.error(`Error processing batch ${i / batchSize + 1}:`, error);
        }
      }

      if (hasValidPrices) {
        console.log('Successfully fetched prices:', newPrices);
        setLivePrices(newPrices);
        setLastPriceUpdate(new Date());

        // Calculate current total value
        const newTotalValue = investments.reduce((sum, investment) => {
          const livePrice = newPrices[investment.name]?.price;
          if (livePrice) {
            return sum + (investment.units * livePrice);
          }
          return sum + investment.value;
        }, 0);

        setCurrentTotalValue(newTotalValue);
      } else {
        const errorMsg = 'No valid prices could be fetched. Please try again later.';
        console.error(errorMsg);
        setPriceError(errorMsg);
        setCurrentTotalValue(totalValue);
      }

    } catch (error) {
      console.error('Error in fetchLivePrices:', error);
      setPriceError('Failed to fetch prices. Please try again later.');
      setCurrentTotalValue(totalValue);
    }
  }, [investments, totalValue]);

  // Update the handleRefreshPrices function
  const handleRefreshPrices = async () => {
    if (livePriceLoading) return; // Prevent multiple simultaneous requests

    // Reset states before starting
    setLivePrices({});
    setPriceError(null);
    setLastPriceUpdate(null);
    setLivePriceLoading(true);

    try {
      await fetchLivePrices();
    } catch (error) {
      console.error('Error refreshing prices:', error);
      setPriceError('Failed to refresh prices. Please try again.');
    } finally {
      setLivePriceLoading(false);
    }
  };

  // Add loading indicator component
  const PriceLoadingIndicator = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={16} />
      <Typography variant="caption" color="text.secondary">
        Fetching prices...
      </Typography>
    </Box>
  );

  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  // Add handleSort function
  // Sell stock handlers
  const handleOpenSellModal = (stock, event) => {
    event.stopPropagation();
    setSelectedStockForSale(stock);
    setSellPrice('');
    setSellUnits('');
    setSellDate(new Date());
    setSellCharges('0');
    setSellTotalAmount('');
    setSellNetAmount('');
    setSellProfitLoss('');
    setOpenSellModal(true);
  };

  const handleCloseSellModal = () => {
    setOpenSellModal(false);
    setSelectedStockForSale(null);
  };

  const handleSellStock = async () => {
    if (!user) {
      alert('You must be logged in to sell stocks');
      return;
    }

    if (!sellPrice || !sellUnits || !sellDate) {
      alert('Please fill all required fields');
      return;
    }

    const unitsToSell = parseFloat(sellUnits);
    if (unitsToSell > selectedStockForSale.units) {
      alert(`Cannot sell ${unitsToSell} units. You only have ${selectedStockForSale.units} units available.`);
      return;
    }

    try {
      setLoading(true);

      // Calculate FIFO cost basis for the sale record
      const fifoCostBasis = calculateFIFOCostBasis;
      const avgBuyPriceForSale = fifoCostBasis / unitsToSell;

      // Prepare sale data
      const saleData = {
        user_id: user.id,
        stock_name: selectedStockForSale.name,
        sale_date: toDateString(sellDate),
        units_sold: unitsToSell,
        sale_price: parseFloat(sellPrice),
        total_amount: parseFloat(sellTotalAmount),
        charges: parseFloat(sellCharges) || 0,
        net_amount: parseFloat(sellNetAmount),
        profit_loss: parseFloat(sellProfitLoss),
        average_buy_price: avgBuyPriceForSale,
        avg_buy_date: getFIFOAverageBuyDate(selectedStockForSale, unitsToSell)
      };

      // Insert sale record
      const saleError = await insertSaleRecord(supabase, saleData);

      if (saleError) {
        console.error('Error recording sale:', saleError);
        throw saleError;
      }

      // Now we need to reduce the units from the stock investments
      // We'll use FIFO (First In, First Out) method
      let remainingUnitsToSell = unitsToSell;
      const transactions = [...selectedStockForSale.transactions].sort((a, b) =>
        new Date(a.purchaseDate) - new Date(b.purchaseDate)
      );

      for (const transaction of transactions) {
        if (remainingUnitsToSell <= 0) break;

        const transactionUnits = parseFloat(transaction.units);

        if (transactionUnits <= remainingUnitsToSell) {
          // Delete this transaction entirely
          const { error: deleteError } = await supabase
            .from('stocks_investments')
            .delete()
            .eq('id', transaction.id);

          if (deleteError) throw deleteError;

          remainingUnitsToSell -= transactionUnits;
        } else {
          // Reduce units in this transaction
          const newUnits = transactionUnits - remainingUnitsToSell;
          const newAmount = (transaction.value / transactionUnits) * newUnits;

          const { error: updateError } = await supabase
            .from('stocks_investments')
            .update({
              units: newUnits,
              amount_invested: newAmount
            })
            .eq('id', transaction.id);

          if (updateError) throw updateError;

          remainingUnitsToSell = 0;
        }
      }

      // Refresh data
      await fetchInvestments();
      handleCloseSellModal();

      alert(`Successfully sold ${unitsToSell} units of ${selectedStockForSale.name}`);

    } catch (error) {
      console.error('Error selling stock:', error);
      alert(`Failed to sell stock: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedInvestments = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc'
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'allocation') {
        const aAllocation = (a.value / totalValue) * 100;
        const bAllocation = (b.value / totalValue) * 100;
        return direction === 'asc'
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      } else if (key === 'currentValue') {
        const aCurrentValue = livePrices[a.name]?.price ? a.units * livePrices[a.name].price : a.value;
        const bCurrentValue = livePrices[b.name]?.price ? b.units * livePrices[b.name].price : b.value;
        return direction === 'asc'
          ? aCurrentValue - bCurrentValue
          : bCurrentValue - aCurrentValue;
      } else if (key === 'currentAllocation') {
        const aCurrentValue = livePrices[a.name]?.price ? a.units * livePrices[a.name].price : a.value;
        const bCurrentValue = livePrices[b.name]?.price ? b.units * livePrices[b.name].price : b.value;
        const aCurrentAllocation = (aCurrentValue / currentTotalValue) * 100;
        const bCurrentAllocation = (bCurrentValue / currentTotalValue) * 100;
        return direction === 'asc'
          ? aCurrentAllocation - bCurrentAllocation
          : bCurrentAllocation - aCurrentAllocation;
      } else if (key === 'pl') {
        const aCurrentValue = livePrices[a.name]?.price ? a.units * livePrices[a.name].price : a.value;
        const bCurrentValue = livePrices[b.name]?.price ? b.units * livePrices[b.name].price : b.value;
        const aPL = aCurrentValue - a.value;
        const bPL = bCurrentValue - b.value;
        return direction === 'asc'
          ? aPL - bPL
          : bPL - aPL;
      }
      return 0;
    });

    setInvestments(sortedInvestments);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/investments/equity')}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box data-tour="inv-prod-summary">
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Stocks
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  Invested: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0,
                  }).format(totalValue)}
                </Typography>
                {livePriceLoading ? (
                  <PriceLoadingIndicator />
                ) : priceError ? (
                  <Chip
                    icon={<ErrorIcon />}
                    label={priceError}
                    color="error"
                    variant="outlined"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                ) : Object.keys(livePrices).length > 0 ? (
                  <>
                    <Typography variant="subtitle1" color="text.secondary">
                      | Current Value: {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(currentTotalValue)}
                    </Typography>
                    {lastPriceUpdate && (
                      <Tooltip title={`Last updated: ${lastPriceUpdate.toLocaleTimeString()}`}>
                        <Chip
                          icon={<AccessTimeIcon />}
                          label="Live"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <Chip
                    icon={<ErrorIcon />}
                    label="No live prices available"
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <TourButton onClick={tour.start} />
            <Button
              data-tour="inv-stocks-plreport"
              variant="outlined"
              startIcon={<ReceiptIcon />}
              onClick={() => navigate('/investments/equity/stocks/pl-report')}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              P/L Report
            </Button>

            <Button
              data-tour="inv-prod-live"
              variant="outlined"
              startIcon={livePriceLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleRefreshPrices}
              disabled={livePriceLoading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                minWidth: 140,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {livePriceLoading ? 'Updating...' : 'Get Live Price'}
            </Button>

            <Button
              data-tour="inv-prod-add"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: 2,
              }}
            >
              Add Investment
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : investments.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4,
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6">No Stocks Found</Typography>
            <Typography variant="body2" sx={{ mb: 3, maxWidth: 400 }}>
              No stock investments found. Start investing to see them here.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Add Your First Investment
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper
                data-tour="inv-prod-holdings"
                sx={{
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  boxShadow: 'var(--fx-card-shadow)',
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Stock Details
                  </Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          onClick={() => handleSort('name')}
                          sx={{
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          Stock Name
                          {sortConfig.key === 'name' && (
                            <span style={{ marginLeft: 8 }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort('value')}
                          sx={{
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          Invested Value
                          {sortConfig.key === 'value' && (
                            <span style={{ marginLeft: 8 }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort('currentValue')}
                          sx={{
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          Current Value
                          {sortConfig.key === 'currentValue' && (
                            <span style={{ marginLeft: 8 }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort('pl')}
                          sx={{
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          P/L
                          {sortConfig.key === 'pl' && (
                            <span style={{ marginLeft: 8 }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort('allocation')}
                          sx={{
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          Invested %
                          {sortConfig.key === 'allocation' && (
                            <span style={{ marginLeft: 8 }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort('currentAllocation')}
                          sx={{
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          Current %
                          {sortConfig.key === 'currentAllocation' && (
                            <span style={{ marginLeft: 8 }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {investments.map((investment, index) => {
                        const livePrice = livePrices[investment.name];
                        const currentValue = livePrice ? investment.units * livePrice.price : investment.value;
                        const changePercent = livePrice ? livePrice.changePercent : 0;
                        const investedPercentage = ((investment.value / totalValue) * 100).toFixed(1);
                        const currentPercentage = ((currentValue / currentTotalValue) * 100).toFixed(1);
                        const profitLoss = currentValue - investment.value;
                        const profitLossPercent = ((profitLoss / investment.value) * 100).toFixed(1);

                        return (
                          <TableRow
                            key={investment.id}
                            onClick={() => handleOpenTransactionHistory(investment)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: COLORS[index % COLORS.length],
                                    mr: 2,
                                  }}
                                />
                                {investment.name}
                                {livePrice && (
                                  <Chip
                                    icon={changePercent >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                    label={`₹${livePrice.price.toFixed(2)} (${changePercent.toFixed(2)}%)`}
                                    color={changePercent >= 0 ? 'success' : 'error'}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 2 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                              }).format(investment.value)}
                            </TableCell>
                            <TableCell align="right">
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                              }).format(currentValue)}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                sx={{
                                  color: profitLoss >= 0 ? 'success.main' : 'error.main',
                                  fontWeight: 500,
                                }}
                              >
                                {profitLoss >= 0 ? '+' : ''}
                                {new Intl.NumberFormat('en-IN', {
                                  style: 'currency',
                                  currency: 'INR',
                                  maximumFractionDigits: 0,
                                  signDisplay: 'never'
                                }).format(Math.abs(profitLoss))}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: profitLoss >= 0 ? 'success.main' : 'error.main',
                                  }}
                                >
                                  {profitLoss >= 0 ? '+' : ''}{profitLossPercent}%
                                </Typography>
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {investedPercentage}%
                            </TableCell>
                            <TableCell align="right">
                              {currentPercentage}%
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="View transactions">
                                  <IconButton size="small" sx={{ color: 'primary.main' }}>
                                    <ReceiptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Sell stock">
                                  <IconButton
                                    data-tour={index === 0 ? 'inv-stocks-sell' : undefined}
                                    size="small"
                                    sx={{ color: 'success.main' }}
                                    onClick={(e) => handleOpenSellModal(investment, e)}
                                  >
                                    <SellIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Single Total row with thicker border */}
                      <TableRow
                        sx={{
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                          '& td': { borderBottom: '2px solid', borderColor: 'divider' }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(totalValue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(currentTotalValue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {(() => {
                            // Show 0 P/L if no live prices are available
                            if (Object.keys(livePrices).length === 0) {
                              return (
                                <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                  ₹0
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      color: 'text.secondary',
                                    }}
                                  >
                                    0.0%
                                  </Typography>
                                </Typography>
                              );
                            }

                            const totalPL = currentTotalValue - totalValue;
                            const totalPLPercent = ((totalPL / totalValue) * 100).toFixed(1);
                            const isProfit = totalPL >= 0;

                            return (
                              <Typography
                                sx={{
                                  color: isProfit ? 'success.main' : 'error.main',
                                  fontWeight: 600,
                                }}
                              >
                                {isProfit ? '+' : ''}
                                {new Intl.NumberFormat('en-IN', {
                                  style: 'currency',
                                  currency: 'INR',
                                  maximumFractionDigits: 0,
                                  signDisplay: 'never'
                                }).format(Math.abs(totalPL))}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: isProfit ? 'success.main' : 'error.main',
                                  }}
                                >
                                  {isProfit ? '+' : ''}{totalPLPercent}%
                                </Typography>
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>100%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>100%</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4} data-tour="inv-prod-charts">
              <Grid container spacing={2}>
                {/* Current Value Distribution */}
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      borderRadius: 3,
                      bgcolor: 'background.paper',
                      boxShadow: 'var(--fx-card-shadow)',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Current Distribution
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Based on live market prices
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 300,
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                      }}
                    >
                      {pieChartData.currentData.length > 0 && (
                        <PieChart colors={chartPalette}
                          series={[
                            {
                              data: pieChartData.currentData,
                              highlightScope: { faded: 'global', highlighted: 'item' },
                              faded: { innerRadius: 30, additionalRadius: -30 },
                            },
                          ]}
                          height={300}
                          margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                          // slotProps={{
                          //   legend: {
                          //     direction: 'row',
                          //     position: { vertical: 'bottom', horizontal: 'middle' },
                          //     padding: { top: 10, bottom: 0, left: 0, right: 0 },
                          //     itemMarkWidth: 8,
                          //     itemMarkHeight: 8,
                          //     markGap: 5,
                          //     itemGap: 15,
                          //     sx: {
                          //       display: 'flex',
                          //       flexWrap: 'wrap',
                          //       justifyContent: 'center',
                          //       gap: 2,
                          //     },
                          //   },
                          // }}
                          slotProps={{ legend: { hidden: true } }}
                          tooltip={{
                            trigger: 'item',
                            formatter: (params) => {
                              const value = new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                              }).format(params.value);
                              return `${params.name}: ${value} (${params.percentage}%)`;
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Invested Value Distribution */}
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      borderRadius: 3,
                      bgcolor: 'background.paper',
                      boxShadow: 'var(--fx-card-shadow)',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Invested Distribution
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Based on purchase values
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 300,
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                      }}
                    >
                      {pieChartData.investedData.length > 0 && (
                        <PieChart colors={chartPalette}
                          series={[
                            {
                              data: pieChartData.investedData,
                              highlightScope: { faded: 'global', highlighted: 'item' },
                              faded: { innerRadius: 30, additionalRadius: -30 },
                            },
                          ]}
                          height={300}
                          margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                          // slotProps={{
                          //   legend: {
                          //     direction: 'row',
                          //     position: { vertical: 'bottom', horizontal: 'middle' },
                          //     padding: { top: 10, bottom: 0, left: 0, right: 0 },
                          //     itemMarkWidth: 8,
                          //     itemMarkHeight: 8,
                          //     markGap: 5,
                          //     itemGap: 15,
                          //     sx: {
                          //       display: 'flex',
                          //       flexWrap: 'wrap',
                          //       justifyContent: 'center',
                          //       gap: 2,
                          //     },
                          //   },
                          // }}
                          slotProps={{ legend: { hidden: true } }}
                          tooltip={{
                            trigger: 'item',
                            formatter: (params) => {
                              const value = new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                              }).format(params.value);
                              return `${params.name}: ${value} (${params.percentage}%)`;
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Modal for adding new investment */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="add-investment-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Add New Stock Investment
            </Typography>

            <Stack spacing={3}>
              <Autocomplete
                value={stockName}
                onChange={(event, newValue) => setStockName(newValue)}
                freeSolo
                autoHighlight
                options={userStockNames}
                getOptionLabel={(option) => {
                  // Handle both string values and objects
                  return typeof option === 'string' ? option : option;
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter(option =>
                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                  );

                  // Add the user's input as an option if it's not already in the list
                  if (params.inputValue !== '' && !filtered.includes(params.inputValue)) {
                    filtered.push(params.inputValue);
                  }

                  return filtered;
                }}
                groupBy={(option) => {
                  // Custom entered value
                  if (!userStockNames.includes(option)) {
                    return "Custom Entry";
                  }
                  // User's existing stocks
                  else {
                    return "Your Stocks";
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Stock Name"
                    required
                    fullWidth
                    placeholder="Enter or select stock name"
                    helperText="You can type a new stock name or select from your existing stocks"
                  />
                )}
              />

              <DatePicker
                label="Date of Purchase"
                value={purchaseDate}
                onChange={(newValue) => setPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />

              <TextField
                label="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />

              <TextField
                label="Number of Units"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                required
                fullWidth
                type="number"
                sx={numberInputStyle}
              />

              <AmountField
                label="Amount Invested"
                value={amountInvested}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                fullWidth
                helperText="Auto-calculated (Price × Units)"
              />

              <Divider />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Save Investment
                </Button>
              </Box>
            </Stack>
          </Box>
        </Modal>

        {/* Transaction Action Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleTransactionMenuClose}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 150, borderRadius: 2 }
          }}
        >
          {currentTransaction?.type === 'buy' ? (
            <>
              <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
                <EditIcon fontSize="small" color="primary" />
                Edit Purchase
              </MenuItem>
              <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
                <DeleteIcon fontSize="small" color="error" />
                Delete Purchase
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem onClick={handleEditSaleModalOpen} sx={{ gap: 1 }}>
                <EditIcon fontSize="small" color="primary" />
                Edit Sale
              </MenuItem>
              <MenuItem onClick={handleDeleteSaleDialogOpen} sx={{ gap: 1 }}>
                <DeleteIcon fontSize="small" color="error" />
                Delete Sale
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Edit Transaction Modal */}
        <Modal
          open={openEditModal}
          onClose={handleEditModalClose}
          aria-labelledby="edit-transaction-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Edit Transaction
            </Typography>

            <Stack spacing={3}>
              <Typography variant="subtitle1">
                Stock: {currentTransaction?.name}
              </Typography>

              <DatePicker
                label="Date of Purchase"
                value={editPurchaseDate}
                onChange={(newValue) => setEditPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />

              <TextField
                label="Price"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />

              <TextField
                label="Number of Units"
                value={editUnits}
                onChange={(e) => setEditUnits(e.target.value)}
                required
                fullWidth
                type="number"
                sx={numberInputStyle}
              />

              <AmountField
                label="Amount Invested"
                value={editAmountInvested}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                fullWidth
                helperText="Auto-calculated (Price × Units)"
              />

              <Divider />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Stack>
          </Box>
        </Modal>

        {/* Edit Sale */}
        <EditSaleDialog
          open={Boolean(editingSaleId)}
          saleId={editingSaleId}
          onClose={() => setEditingSaleId(null)}
          onSaved={handleSaleEdited}
        />

        {/* Delete Sale Confirmation Dialog */}
        <Dialog
          open={openDeleteSaleDialog}
          onClose={handleDeleteSaleDialogClose}
          aria-labelledby="delete-sale-dialog"
        >
          <DialogTitle id="delete-sale-title">
            Confirm Delete Sale Transaction
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-sale-description">
              Are you sure you want to delete this sale transaction? The sold units will be restored to your inventory.
            </DialogContentText>
            {currentTransaction && currentTransaction.type === 'sell' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sale Details:
                </Typography>
                <Typography variant="body2">
                  Stock: {selectedStock?.name}<br />
                  Date: {currentTransaction.date ? new Date(currentTransaction.date).toLocaleDateString('en-IN') : '-'}<br />
                  Units: {Math.abs(currentTransaction.units).toFixed(2)}<br />
                  Amount: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0,
                  }).format(currentTransaction.amount)}<br />
                  P/L: {currentTransaction.profitLoss !== undefined && (
                    <span style={{ 
                      color: currentTransaction.profitLoss >= 0 ? '#2e7d32' : '#d32f2f',
                      fontWeight: 600
                    }}>
                      {currentTransaction.profitLoss >= 0 ? '+' : ''}
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(currentTransaction.profitLoss)}
                    </span>
                  )}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleDeleteSaleDialogClose}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteSale}
              variant="contained"
              color="error"
              sx={{ borderRadius: 2, textTransform: 'none' }}
              autoFocus
            >
              Delete Sale
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteDialogClose}
          aria-labelledby="delete-transaction-dialog"
        >
          <DialogTitle id="alert-dialog-title">
            Confirm Delete Transaction
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this transaction? This action cannot be undone.
            </DialogContentText>
            {currentTransaction && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Transaction Details:
                </Typography>
                <Typography variant="body2">
                  Stock: {currentTransaction.name || selectedStock?.name || '-'}<br />
                  Purchase Date: {currentTransaction.purchaseDate
                    ? new Date(`${String(currentTransaction.purchaseDate).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN')
                    : '-'}<br />
                  Units: {Number(currentTransaction.units || 0).toFixed(2)}<br />
                  Price per Unit: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(Number(currentTransaction.price) || 0)}<br />
                  Amount: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0,
                  }).format(Number(currentTransaction.value ?? currentTransaction.amount) || 0)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleDeleteDialogClose}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTransaction}
              variant="contained"
              color="error"
              sx={{ borderRadius: 2, textTransform: 'none' }}
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sell Stock Modal */}
        <Modal
          open={openSellModal}
          onClose={handleCloseSellModal}
          aria-labelledby="sell-stock-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '85%', md: 700 },
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
          }}>
            <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Sell Stock
            </Typography>

            {selectedStockForSale && (
              <Box>
                {/* Stock Info Header */}
                <Box sx={{
                  p: 2,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  mb: 3
                }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Stock Name
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {selectedStockForSale.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Available Units
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {selectedStockForSale.units.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Average Buy Price
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        ₹{(selectedStockForSale.value / selectedStockForSale.units).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Two Column Form */}
                <Grid container spacing={3}>
                  {/* Left Column */}
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={3}>
                      <DatePicker
                        label="Sale Date"
                        value={sellDate}
                        onChange={(newValue) => setSellDate(newValue)}
                        slotProps={{
                          textField: { fullWidth: true, required: true }
                        }}
                      />

                      <TextField
                        label="Sale Price per Unit"
                        value={sellPrice}
                        onChange={(e) => setSellPrice(e.target.value)}
                        required
                        fullWidth
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        sx={numberInputStyle}
                      />

                      <TextField
                        label="Number of Units to Sell"
                        value={sellUnits}
                        onChange={(e) => setSellUnits(e.target.value)}
                        required
                        fullWidth
                        type="number"
                        helperText={`Maximum: ${selectedStockForSale.units.toFixed(2)} units`}
                        sx={numberInputStyle}
                      />

                      <AmountField
                        label="Charges (Brokerage, Tax, etc.)"
                        value={sellCharges}
                        onChange={(e) => setSellCharges(e.target.value)}
                        fullWidth
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        sx={numberInputStyle}
                      />
                    </Stack>
                  </Grid>

                  {/* Right Column - Calculated Values */}
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={3}>
                      <AmountField
                        label="Total Amount"
                        value={sellTotalAmount}
                        InputProps={{
                          readOnly: true,
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        fullWidth
                        helperText="Price × Units"
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: 500
                          }
                        }}
                      />

                      <AmountField
                        label="Net Amount"
                        value={sellNetAmount}
                        InputProps={{
                          readOnly: true,
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        fullWidth
                        helperText="Total Amount - Charges"
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: 500
                          }
                        }}
                      />

                      <TextField
                        label="Profit/Loss"
                        value={sellProfitLoss}
                        InputProps={{
                          readOnly: true,
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        fullWidth
                        helperText="Net Amount - Cost Basis"
                        sx={{
                          '& .MuiInputBase-input': {
                            color: sellProfitLoss && parseFloat(sellProfitLoss) >= 0
                              ? 'success.main'
                              : 'error.main',
                            fontWeight: 700,
                            fontSize: '1.1rem'
                          },
                          '& .MuiInputBase-root': {
                            bgcolor: sellProfitLoss
                              ? parseFloat(sellProfitLoss) >= 0
                                ? alpha('#2e7d32', 0.05)
                                : alpha('#d32f2f', 0.05)
                              : 'transparent'
                          }
                        }}
                      />

                      {/* Summary Box */}
                      {sellProfitLoss && (
                        <Box sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: parseFloat(sellProfitLoss) >= 0
                            ? alpha('#2e7d32', 0.1)
                            : alpha('#d32f2f', 0.1),
                          border: 1,
                          borderColor: parseFloat(sellProfitLoss) >= 0
                            ? 'success.main'
                            : 'error.main'
                        }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {parseFloat(sellProfitLoss) >= 0 ? 'Expected Profit' : 'Expected Loss'}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: parseFloat(sellProfitLoss) >= 0
                                ? 'success.main'
                                : 'error.main'
                            }}
                          >
                            {parseFloat(sellProfitLoss) >= 0 ? '+' : ''}
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 2,
                            }).format(parseFloat(sellProfitLoss))}
                          </Typography>
                          {sellUnits && selectedStockForSale && calculateFIFOCostBasis > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {((parseFloat(sellProfitLoss) / calculateFIFOCostBasis) * 100).toFixed(2)}% return
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    variant="outlined"
                    onClick={handleCloseSellModal}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSellStock}
                    color="success"
                    disabled={!sellPrice || !sellUnits || parseFloat(sellUnits) > selectedStockForSale.units}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Confirm Sale
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Modal>

        {/* Modal for viewing transaction history */}
        <Modal
          open={openHistoryModal}
          onClose={handleCloseHistoryModal}
          aria-labelledby="transaction-history-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '85%', lg: 1000 },
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  {selectedStock?.name}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Invested Amount: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0,
                  }).format(selectedStock?.value || 0)} |
                  Total Units: {(selectedStock?.units || 0).toFixed(2)} |
                  Average Price: ₹{(() => {
                    if (stockTransactions.length === 0) return '0.00';
                    // Only calculate average from buy transactions
                    const buyTransactions = stockTransactions.filter(tx => tx.type === 'buy');
                    if (buyTransactions.length === 0) return '0.00';
                    const totalAmount = buyTransactions.reduce((sum, tx) => sum + (tx.amount || 0), 0);
                    const totalUnits = buyTransactions.reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0);
                    return totalUnits > 0 ? (totalAmount / totalUnits).toFixed(2) : '0.00';
                  })()}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseHistoryModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Chip
                icon={<ReceiptIcon fontSize="small" />}
                label="Transaction History"
                color="primary"
                variant="outlined"
                sx={{ borderRadius: 1 }}
              />
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }}>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 700, py: 2 }}>Type</TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 700, py: 2 }}>Date</TableCell>
                    <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 700, py: 2 }}>Price (₹)</TableCell>
                    <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 700, py: 2 }}>Units</TableCell>
                    <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 700, py: 2 }}>Amount (₹)</TableCell>
                    <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 700, py: 2 }}>P/L (₹)</TableCell>
                    <TableCell align="center" sx={{ color: 'text.primary', fontWeight: 700, py: 2, width: 80 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockTransactions.length > 0 ? (
                    stockTransactions.map((transaction, index) => (
                      <TableRow 
                        key={transaction.id}
                        sx={{
                          bgcolor: transaction.type === 'sell' 
                            ? alpha('#f57c00', 0.08)
                            : index % 2 === 0 
                              ? alpha('#000', 0.02)
                              : 'transparent',
                          '&:hover': {
                            bgcolor: transaction.type === 'sell'
                              ? alpha('#f57c00', 0.12)
                              : alpha('#000', 0.04)
                          }
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Chip
                            label={transaction.type === 'buy' ? 'Buy' : 'Sell'}
                            size="small"
                            color={transaction.type === 'buy' ? 'primary' : 'warning'}
                            sx={{ 
                              minWidth: 65,
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">
                            {transaction.date
                              ? new Date(transaction.date).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {transaction.price
                              ? `₹${transaction.price.toFixed(2)}`
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: transaction.type === 'sell' ? 'error.main' : 'text.primary',
                              fontWeight: transaction.type === 'sell' ? 700 : 500
                            }}
                          >
                            {transaction.units
                              ? Math.abs(parseFloat(transaction.units)).toFixed(2)
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(transaction.amount)}
                          </Typography>
                          {transaction.charges > 0 && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              Charges: ₹{transaction.charges.toFixed(2)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          {transaction.type === 'sell' && transaction.profitLoss !== undefined ? (
                            <Box sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: transaction.profitLoss >= 0 
                                ? alpha('#2e7d32', 0.1)
                                : alpha('#d32f2f', 0.1)
                            }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: transaction.profitLoss >= 0 ? 'success.main' : 'error.main',
                                  fontWeight: 700
                                }}
                              >
                                {transaction.profitLoss >= 0 ? '+' : ''}
                                {new Intl.NumberFormat('en-IN', {
                                  style: 'currency',
                                  currency: 'INR',
                                  maximumFractionDigits: 0,
                                }).format(transaction.profitLoss)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                            sx={{
                              '&:hover': {
                                bgcolor: alpha('#1976d2', 0.1)
                              }
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No transaction history available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {stockTransactions.length > 0 && (
                    <TableRow sx={{ 
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                      borderTop: 2,
                      borderColor: 'divider'
                    }}>
                      <TableCell sx={{ fontWeight: 700, py: 2.5 }} colSpan={2}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Summary
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2.5 }} />
                      <TableCell align="right" sx={{ py: 2.5 }}>
                        <Box>
                          {(() => {
                            const totalBought = stockTransactions
                              .filter(tx => tx.type === 'buy')
                              .reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0);
                            const totalSold = stockTransactions
                              .filter(tx => tx.type === 'sell')
                              .reduce((sum, tx) => sum + Math.abs(parseFloat(tx.units || 0)), 0);
                            return (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {totalBought.toFixed(2)} bought
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {totalSold.toFixed(2)} sold
                                </Typography>
                              </>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2.5 }} />
                      <TableCell align="right" sx={{ py: 2.5 }}>
                        {(() => {
                          const totalPL = stockTransactions
                            .filter(tx => tx.type === 'sell')
                            .reduce((sum, tx) => sum + (tx.profitLoss || 0), 0);
                          return totalPL !== 0 ? (
                            <Box sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 2,
                              py: 1,
                              borderRadius: 1.5,
                              bgcolor: totalPL >= 0 
                                ? alpha('#2e7d32', 0.15)
                                : alpha('#d32f2f', 0.15)
                            }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: totalPL >= 0 ? 'success.main' : 'error.main',
                                  fontWeight: 700
                                }}
                              >
                                {totalPL >= 0 ? '+' : ''}
                                {new Intl.NumberFormat('en-IN', {
                                  style: 'currency',
                                  currency: 'INR',
                                  maximumFractionDigits: 0,
                                }).format(totalPL)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCloseHistoryModal}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
        <GuidedTour steps={TOURS['inv-equity-stocks']} open={tour.open} onClose={tour.close} />
      </Box>
    </LocalizationProvider>
  );
};

export default Stocks;